// Every error in plain words: what happened, why, what to do, one button. Technical details
// (code, request id, provider text) go into the collapsed "Подробнее" block.
import type { AdminJob } from '@fonus/shared';
import { ApiFailure } from './api.ts';

export interface HumanError {
  title: string;
  why: string;
  todo: string;
  action?: { label: string; href: string };
  technical: { code: string | null; requestId: string | null; text: string | null };
}

const PERMISSION: Record<string, string> = {
  text_to_speech: 'Text to Speech',
  voices_read: 'Voices: Read',
  user_read: 'User: Read',
};
const LANG: Record<string, string> = { uz: 'узбекского', ru: 'русского', en: 'английского' };
const str = (v: unknown, def = ''): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : def;
const fmt = (n: unknown) =>
  typeof n === 'number' ? new Intl.NumberFormat('ru-RU').format(n) : '—';

export function describe(
  code: string | null,
  detail: Record<string, unknown> = {},
): Omit<HumanError, 'technical'> {
  const toHome = { label: 'Открыть «Готовность»', href: '#/' };
  const toVoices = { label: 'Перейти к голосам', href: '#/voices' };
  const toSettings = { label: 'Открыть настройки', href: '#/settings' };
  switch (code) {
    case 'NO_VOICE': {
      const langs =
        (detail.langs as string[] | undefined) ?? (detail.lang ? [str(detail.lang)] : []);
      return {
        title: 'Не выбран голос',
        why: `Для ${langs.map((l) => LANG[l] ?? l).join(', ')} языка нет голоса.`,
        todo: 'Выберите голос на странице «Голоса», затем нажмите «Повторить».',
        action: toVoices,
      };
    }
    case 'BUSY':
      return {
        title: 'Уже идёт другая задача',
        why: 'Одновременно может идти только одна генерация или озвучка — так кредиты не тратятся дважды.',
        todo: 'Дождитесь окончания (прогресс — в «Журнале») или отмените её.',
        action: { label: 'Открыть журнал', href: '#/journal' },
      };
    case 'PAUSED':
      return {
        title: 'Генерация остановлена',
        why: 'Включена пауза — новые задачи не запускаются.',
        todo: 'Снимите паузу на главной (если её включили в Railway — уберите там CONTENT_PAUSED).',
        action: toHome,
      };
    case 'ELEVENLABS_PERMISSION': {
      const p = PERMISSION[str(detail.permission)] ?? str(detail.permission, 'нужного');
      return {
        title: `У ключа ElevenLabs нет права ${p}`,
        why: 'ElevenLabs отклонил запрос: ключ создан без этого права.',
        todo: `В ElevenLabs создайте ключ с правами Text to Speech, Voices: Read и User: Read и замените ELEVENLABS_API_KEY в Railway → worker.`,
        action: toHome,
      };
    }
    case 'ELEVENLABS_AUTH':
      return {
        title: 'ElevenLabs не принял ключ',
        why: 'Ключ неверный, удалён или истёк.',
        todo: 'Создайте новый ключ в ElevenLabs и замените ELEVENLABS_API_KEY в Railway → worker.',
        action: toHome,
      };
    case 'ELEVENLABS_QUOTA':
    case 'BUDGET_PROVIDER':
      return {
        title: 'Не хватает кредитов ElevenLabs',
        why: `Нужно ≈${fmt(detail.need)}, на счёте осталось ${fmt(detail.remaining)}.`,
        todo: 'Пополните тариф ElevenLabs или дождитесь обновления кредитов, затем нажмите «Повторить».',
      };
    case 'BUDGET_MONTHLY':
      return {
        title: 'Месячный лимит кредитов исчерпан',
        why: `За месяц потрачено ${fmt(detail.monthUsed)} из ${fmt(detail.limit)}, нужно ещё ≈${fmt(detail.need)}.`,
        todo: 'Увеличьте лимит в настройках или дождитесь следующего месяца.',
        action: toSettings,
      };
    case 'BUDGET_UNAVAILABLE':
      return {
        title: 'Не удалось проверить баланс ElevenLabs',
        why: 'Без проверки баланса озвучка не запускается, чтобы не уйти в минус.',
        todo: 'Проверьте, что у ключа есть право User: Read, и нажмите «Повторить».',
        action: toHome,
      };
    case 'ELEVENLABS_VOICE':
      return {
        title: 'Голос недоступен',
        why: 'ElevenLabs не нашёл выбранный голос (удалён или ID с ошибкой).',
        todo: 'Выберите другой голос для этого языка.',
        action: toVoices,
      };
    case 'CLAUDE_AUTH':
    case 'CLAUDE_PERMISSION':
      return {
        title: 'Claude не принял ключ',
        why: 'Ключ Anthropic неверный, отозван или без доступа.',
        todo: 'Замените ANTHROPIC_API_KEY в Railway → worker и нажмите «Проверить снова».',
        action: toHome,
      };
    case 'CLAUDE_MODEL':
      return {
        title: 'Модель Claude недоступна',
        why: 'Выбранной модели нет для этого ключа.',
        todo: 'Выберите другую модель в настройках.',
        action: toSettings,
      };
    case 'CLAUDE_REFUSAL':
      return {
        title: 'Claude отказался писать эту сказку',
        why: 'Тема показалась модели неподходящей.',
        todo: 'Измените тему и запустите заново.',
      };
    case 'CLAUDE_BAD_ANSWER':
      return {
        title: 'Claude прислал неполный ответ',
        why: 'Ответ обрезался или не прошёл проверку формата.',
        todo: 'Обычно помогает повтор — он запускается автоматически до 2 раз.',
      };
    case 'RATE_LIMITED':
      return {
        title: `${str(detail.provider, 'Сервис')} просит подождать`,
        why: 'Слишком много запросов подряд.',
        todo: 'Повтор запустится автоматически; если нет — нажмите «Повторить» через минуту.',
      };
    case 'PROVIDER_DOWN':
      return {
        title: `${str(detail.provider, 'Сервис')} временно не отвечает`,
        why: 'Ошибка на стороне провайдера.',
        todo: 'Повтор запустится автоматически; если не поможет — попробуйте позже.',
      };
    case 'NETWORK':
      return {
        title: 'Нет связи',
        why: 'Сервер не смог связаться с провайдером (или браузер — с сервером).',
        todo: 'Проверьте интернет и нажмите «Повторить».',
      };
    case 'CANCELLED':
      return {
        title: 'Задача отменена',
        why: 'Её отменили вручную или кнопкой «Остановить всё».',
        todo: 'Если нужно — нажмите «Повторить».',
      };
    case 'AUDIO_PROCESSING':
      return {
        title: 'Не удалось обработать звук',
        why: 'ffmpeg не смог собрать аудио.',
        todo: 'Нажмите «Повторить»; если ошибка повторится — напишите разработчику, приложив «Подробнее».',
      };
    case 'UPLOAD_MISSING':
      return {
        title: 'Файл не найден в хранилище',
        why: 'Загрузка не завершилась или файл удалён.',
        todo: 'Загрузите аудио ещё раз.',
      };
    case 'UPLOAD_INVALID':
      return {
        title: 'Файл не читается как аудио',
        why: 'Файл повреждён или это не mp3 / wav / m4a.',
        todo: 'Проверьте файл (он должен проигрываться на компьютере) и загрузите заново.',
      };
    case 'UPLOAD_FORMAT':
      return {
        title: 'Неподходящий формат',
        why: 'Принимаются только mp3, wav и m4a.',
        todo: 'Сохраните запись в одном из этих форматов.',
      };
    case 'UPLOAD_TOO_LARGE':
      return {
        title: 'Файл слишком большой',
        why: 'Максимум — 200 МБ.',
        todo: 'Сохраните запись в mp3 (128–192 кбит/с): 15 минут ≈ 15–20 МБ.',
      };
    case 'UPLOAD_EMPTY':
      return { title: 'Пустой файл', why: 'В файле нет данных.', todo: 'Выберите другой файл.' };
    case 'DIRECTOR_CHANGED_TEXT':
      return {
        title: 'Claude изменил слова сказки',
        why: 'Расставлять можно только теги — сценарий с изменёнными словами не сохраняется.',
        todo: 'Повтор запускается автоматически; если не поможет — расставьте теги вручную.',
      };
    case 'SCRIPT_CHANGES_TEXT':
      return {
        title: 'В сценарии изменились слова',
        why: 'В сценарии озвучки можно только добавлять теги в квадратных скобках.',
        todo: 'Верните слова как в тексте или нажмите «Сбросить сценарий».',
      };
    case 'NOT_VERIFIED':
      return {
        title: 'Вычитайте uz-текст',
        why: 'Узбекский текст распознан из аудио и ещё не проверен человеком.',
        todo: 'Прочитайте текст, исправьте ошибки и поставьте «Текст вычитан ✓».',
      };
    case 'NOT_READY':
      return {
        title: 'Озвучка не готова',
        why: 'Не все языки озвучены или текст менялся после озвучки.',
        todo: 'Переозвучьте отмеченные языки.',
      };
    case 'BAD_STATUS':
      return {
        title: 'Недоступно для этой сказки',
        why: 'Это действие нельзя сделать в текущем статусе (например, удалить опубликованную).',
        todo: 'Обновите страницу и проверьте статус сказки.',
      };
    case 'FORBIDDEN':
      return {
        title: 'Недостаточно прав',
        why: 'Это действие доступно только владельцу.',
        todo: 'Попросите владельца выполнить его.',
      };
    default:
      return {
        title: 'Что-то пошло не так',
        why: 'Непредвиденная ошибка.',
        todo: 'Нажмите «Повторить». Если повторится — пришлите разработчику блок «Подробнее».',
      };
  }
}

export function humanFromApi(err: unknown): HumanError {
  if (err instanceof ApiFailure) {
    const code = err.status === 403 && !err.reason ? 'FORBIDDEN' : err.reason;
    return {
      ...describe(code, err.detail),
      technical: {
        code: err.reason ?? String(err.status),
        requestId: err.requestId,
        text: err.message,
      },
    };
  }
  return {
    ...describe(null),
    technical: {
      code: null,
      requestId: null,
      text: err instanceof Error ? err.message : String(err),
    },
  };
}

export function humanFromJob(job: AdminJob): HumanError {
  return {
    ...describe(job.errorCode, job.errorDetail ?? {}),
    technical: { code: job.errorCode, requestId: null, text: job.error },
  };
}
